import React from 'react';
import { X, Zap, Mail, Calendar, Users, BarChart3, Settings, Check, Bell, Info, Eye, Send, Copy } from 'lucide-react';
import { NotificationRecord } from '@/services/PagesServices/MarketingAndNotificationService';
import './NotificationModal.css';

interface NotificationModalProps {
  notification: NotificationRecord | null;
  onClose: () => void;
}

const NotificationModal: React.FC<NotificationModalProps> = ({ notification, onClose }) => {
  if (!notification) return null;

  const getTypeLabel = (type: string) => {
    if (type === 'email') return 'Email';
    if (type === 'in-app') return 'In-App';
    return 'Both';
  };

  return (
    <div className="s-admin-marketing-modal-overlay" onClick={onClose}>
      <div className="s-admin-marketing-modal" onClick={(e) => e.stopPropagation()}>
        <div className="s-admin-marketing-modal-header">
          <div className="s-admin-marketing-modal-title-row">
            <div className="s-admin-marketing-modal-icon">
              <Zap size={24} />
            </div>
            <div>
              <h2 className="s-admin-marketing-modal-title">{notification.title}</h2>
              <div className="s-admin-marketing-modal-meta">
                <span className="s-admin-marketing-modal-status">{notification.status.charAt(0).toUpperCase() + notification.status.slice(1)}</span>
                <span>•</span>
                <span className="s-admin-marketing-modal-type">{getTypeLabel(notification.type)}</span>
              </div>
            </div>
          </div>
          <button className="s-admin-marketing-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="s-admin-marketing-modal-body">
          <div className="s-admin-marketing-modal-section">
            <div className="s-admin-marketing-modal-section-header">
              <Mail size={16} className="s-admin-marketing-section-icon" />
              <h3>Message Content</h3>
            </div>
            <div className="s-admin-marketing-modal-message">
              {notification.message}
            </div>
          </div>

          <div className="s-admin-marketing-modal-row">
            <div className="s-admin-marketing-modal-section s-admin-marketing-modal-half">
              <div className="s-admin-marketing-modal-section-header">
                <Calendar size={16} className="s-admin-marketing-section-icon" />
                <h3>Date & Time</h3>
              </div>
              <div className="s-admin-marketing-date-info">
                <Check size={16} className="s-admin-marketing-check-icon" />
                <div>
                  <div className="s-admin-marketing-date-label-small">Sent Date</div>
                  <div className="s-admin-marketing-date-value">{notification.sentDate || '-'}</div>
                </div>
              </div>
            </div>

            <div className="s-admin-marketing-modal-section s-admin-marketing-modal-half">
              <div className="s-admin-marketing-modal-section-header">
                <Users size={16} className="s-admin-marketing-section-icon" />
                <h3>Target Audience</h3>
              </div>
              <div className="s-admin-marketing-target-badges">
                {notification.targetAudiences.map((aud, idx) => (
                  <span
                    key={idx}
                    className="s-admin-marketing-target-badge"
                    style={{ backgroundColor: aud.color, color: 'white' }}
                  >
                    <Users size={12} />
                    {aud.name}
                  </span>
                ))}
              </div>
              <div className="s-admin-marketing-audience-count">
                {notification.targetAudiences.length} audience segment{notification.targetAudiences.length > 1 ? 's' : ''} targeted
              </div>
            </div>
          </div>

          {notification.openRate && (
            <div className="s-admin-marketing-modal-section">
              <div className="s-admin-marketing-modal-section-header">
                <BarChart3 size={16} className="s-admin-marketing-section-icon" />
                <h3>Performance Metrics</h3>
              </div>
              <div className="s-admin-marketing-metrics-grid">
                <div className="s-admin-marketing-metric-card">
                  <div className="s-admin-marketing-metric-header">
                    <Eye size={16} className="s-admin-marketing-metric-icon-blue" />
                    <span>Open Rate</span>
                  </div>
                  <div className="s-admin-marketing-metric-value">{notification.openRate}%</div>
                  <div className="s-admin-marketing-metric-bar">
                    <div
                      className="s-admin-marketing-metric-fill s-admin-marketing-metric-fill-blue"
                      style={{ width: `${notification.openRate}%` }}
                    ></div>
                  </div>
                  <div className="s-admin-marketing-metric-label">Excellent engagement</div>
                </div>

                <div className="s-admin-marketing-metric-card">
                  <div className="s-admin-marketing-metric-header">
                    <Zap size={16} className="s-admin-marketing-metric-icon-green" />
                    <span>Click Rate</span>
                  </div>
                  <div className="s-admin-marketing-metric-value">{notification.clickRate}%</div>
                  <div className="s-admin-marketing-metric-bar">
                    <div
                      className="s-admin-marketing-metric-fill s-admin-marketing-metric-fill-green"
                      style={{ width: `${notification.clickRate}%` }}
                    ></div>
                  </div>
                  <div className="s-admin-marketing-metric-label">High click-through</div>
                </div>
              </div>
            </div>
          )}

          <div className="s-admin-marketing-modal-section">
            <div className="s-admin-marketing-modal-section-header">
              <Settings size={16} className="s-admin-marketing-section-icon" />
              <h3>Delivery Details</h3>
            </div>
            <div className="s-admin-marketing-delivery-grid">
              <div className="s-admin-marketing-delivery-card">
                <Mail size={18} />
                <div>
                  <div className="s-admin-marketing-delivery-title">Email</div>
                  <div className="s-admin-marketing-delivery-status">
                    <Check size={12} />
                    Enabled
                  </div>
                </div>
              </div>
              <div className="s-admin-marketing-delivery-card">
                <Bell size={18} />
                <div>
                  <div className="s-admin-marketing-delivery-title">In-App</div>
                  <div className="s-admin-marketing-delivery-status">
                    <Check size={12} />
                    Enabled
                  </div>
                </div>
              </div>
              <div className="s-admin-marketing-delivery-card">
                <Zap size={18} />
                <div>
                  <div className="s-admin-marketing-delivery-title">Both Channels</div>
                  <div className="s-admin-marketing-delivery-status">
                    <Check size={12} />
                    Multi-channel
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="s-admin-marketing-modal-section s-admin-marketing-summary-section">
            <div className="s-admin-marketing-modal-section-header">
              <Info size={16} className="s-admin-marketing-section-icon" />
              <h3>Summary</h3>
            </div>
            <div className="s-admin-marketing-summary-grid">
              <div className="s-admin-marketing-summary-item">
                <div className="s-admin-marketing-summary-label">Status</div>
                <div className="s-admin-marketing-summary-value">{notification.status.charAt(0).toUpperCase() + notification.status.slice(1)}</div>
              </div>
              <div className="s-admin-marketing-summary-item">
                <div className="s-admin-marketing-summary-label">Sent Date</div>
                <div className="s-admin-marketing-summary-value">{notification.sentDate || '-'}</div>
              </div>
              <div className="s-admin-marketing-summary-item">
                <div className="s-admin-marketing-summary-label">Audiences</div>
                <div className="s-admin-marketing-summary-value">{notification.targetAudiences.length}</div>
              </div>
              <div className="s-admin-marketing-summary-item">
                <div className="s-admin-marketing-summary-label">Type</div>
                <div className="s-admin-marketing-summary-value">{getTypeLabel(notification.type)}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="s-admin-marketing-modal-footer">
          <button className="s-admin-marketing-modal-btn s-admin-marketing-modal-btn-secondary">
            <Send size={16} />
            Resend
          </button>
          <button className="s-admin-marketing-modal-btn s-admin-marketing-modal-btn-secondary">
            <Copy size={16} />
            Duplicate
          </button>
          <button className="s-admin-marketing-modal-btn s-admin-marketing-modal-btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationModal;
